import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useMediaPreview } from '../../../shared/contexts/MediaPreviewContext';
import { ensureJpeg } from '../../../shared/utils/ensureJpeg';
import { ensureMp4H264Aac } from '../../../shared/utils/videoPipeline';
import { getOverlayForFilter, type FilterPresetId } from '../../../shared/components/filterHelpers.web';
import { useTranslation } from 'react-i18next';
import SidebarGalleryPosting from '../../sidebars/SidebarGalleryPosting'; 
import HighLogo from '../../HighLogo';
import logo from "../../../assets/logo.png";
import VideoPlayerWeb from "./VideoPlayerWeb";
import uploadViaBackendHls from '../../../shared/utils/uploadViaBackendHls';
import type { MediaItem } from '../../../shared/contexts/MediaPreviewContext';

type ExtMediaItem = MediaItem & {
  musicId?: string | null;
  videoId?: string | null;
  isHls?: boolean;
  thumbnailUrl?: string | null;
  posterUrl?: string | null;
  hlsUrl?: string | null;
  dashUrl?: string | null;
};

     
type R2UploadResult =
  | { url: string }
  | {
      videoId: string;
      isHls: true;
      hlsUrl?: string | null;
      dashUrl?: string | null;
      posterUrl?: string | null;
    };


interface SidebarData {
  hashtags?: string[];
}

interface PostLocation {
  city: string;
  country: string;
  countryCode?: string;
}

type Prefs = { analyticsConsent: boolean; pushConsent: boolean; locationConsent: boolean };


const fromServerMedia = (m: any, index: number): ExtMediaItem => {
  const filterId =
    m?.filterId ??
    m?.filter?.id ??
    m?.filter?.name ??
    null;

  const musicId =
    m?.musicId ??
    m?.music?._id ??
    m?.audio?._id ??
    m?.trackId ??
    null;

  return {
    id: `existing-${index}`,
    url: m?.url ?? m?.hlsUrl ?? null,
    videoId: m?.videoId ?? null,
    isHls: Boolean(m?.isHls),
    hlsUrl: m?.hlsUrl ?? m?.url ?? null,
    dashUrl: m?.dashUrl ?? null,
    posterUrl: m?.posterUrl ?? null,
    thumbnailUrl: m?.thumbnailUrl ?? m?.posterUrl ?? null,

    musicId,
    musicTitle:
      m?.musicTitle ??
      m?.songTitle ??
      m?.trackTitle ??
      m?.music?.title ??
      m?.audio?.title ??
      null,

    musicArtist:
      m?.musicArtist ??
      m?.songArtist ??
      m?.trackArtist ??
      m?.music?.artist ??
      m?.audio?.artist ??
      null,

    filterId,
  };
};


// read a media item's filter from context (or null)
const useFilterLookup = () => {
  const { mediaItems } = useMediaPreview();
  return (id: string) => mediaItems.find(m => m.id === id)?.filterId ?? null;
};

// build overlay style for a given filterId
const overlayStyleFor = (fid?: string | null): React.CSSProperties | null => {
  const id = (fid as FilterPresetId) || 'none';
  const { tint, opacity } = getOverlayForFilter(id);
  return tint ? { position: 'absolute', inset: 0, backgroundColor: tint, opacity, pointerEvents: 'none', borderRadius: 8 } : null;
};

const DAILY_POST_LIMIT = 20;
const MAX_POST_MEDIA_BYTES = Math.round(1.7 * 1024 * 1024 * 1024); // ~1.7 GB
const MAX_MEDIA_PER_POST = 8;
const MAX_HASHTAGS = 8; // 👈 NEW

// 3 minutes + 3 seconds (same as mobile HARD_CAP_SEC = 183)
const HARD_CAP_VIDEO_SEC = 183;
const MAX_VIDEO_DURATION_MS = HARD_CAP_VIDEO_SEC * 1000;
const MIN_VIDEO_DURATION_MS = 2.9 * 1000;

const GalleryPosting = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();

  const currentPath = `${location.pathname}${location.search || ''}`;
  const { t } = useTranslation();
  const UNKNOWN = t("locationChange.unknown", "Unknown");

  const [isLoading, setIsLoading] = useState(false);
  const [userEnteredHashtags, setUserEnteredHashtags] = useState('');
  const [postsLeft, setPostsLeft] = useState<number | null>(null);
  const [postsLeftLoading, setPostsLeftLoading] = useState(false);

  const [orientationMap, setOrientationMap] = useState<Record<string, boolean>>({});

  const [isDragOver, setIsDragOver] = useState(false);

  const [vpnBlockedMessage, setVpnBlockedMessage] = useState<string | null>(null);

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
const [cancelBusy, setCancelBusy] = useState(false);


  const [isTranscoding, setIsTranscoding] = useState(false);
  const { mediaItems, setMediaItems, addOrReplaceMediaItem } = useMediaPreview();
  const { sidebarData }: { sidebarData?: SidebarData } = location.state || {};
  const { editTargetId, previewFilename, isPreview, previewFile } = location.state || {};
  const hasInjectedRef = useRef(false);
  const sidebarInitialized = useRef(false);
  const getFilterId = useFilterLookup();

  const getApiBaseUrl = () => {
    const base = process.env.REACT_APP_API_BASE_URL;
    if (!base) {
      throw new Error("REACT_APP_API_BASE_URL is not defined");
    }
    // remove trailing /api if present, because you later add /api/...
    return base.replace(/\/api\/?$/, '');
  };
 
// in GalleryPosting.tsx (your prepareFileForUpload)
async function prepareFileForUpload(file: File): Promise<File> {
  if (file.type.startsWith('image/')) {
    return await ensureJpeg(file, 0.85);
  }

if (file.type.startsWith('video/')) {
  try {
    const out = await ensureMp4H264Aac(file);
    if (out.type === 'video/mp4' && !/\.mp4$/i.test(out.name)) {
      const base = out.name.replace(/\.[^.]+$/, '');
      return new File([out], `${base}.mp4`, { type: 'video/mp4' });
    }
    return out;
  } catch (e) {
    const base = file.name.replace(/\.[^.]+$/, '');
    return new File([file], `${base}.mp4`, { type: 'video/mp4' });
  }
}

  return file;
}


const isVideoItem = (item: MediaItem & any): boolean => {
  if (item.isHls && item.videoId) return true;
  if (item.file) return item.file.type.startsWith("video/");
  if (item.url) return /\.(mp4|webm|mov|m4v)$/i.test(item.url);
  return false;
};

const toRemoteHttp = (item: any): string | undefined => {
  if (item.isHls) {
    return item.hlsUrl || item.url || (item.videoId
      ? `${getApiBaseUrl()}/hls/playlist/${item.videoId}`
      : undefined);
  }

  const u = item.url;
  if (!u) return undefined;
  if (u.startsWith("blob:")) return undefined;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${getApiBaseUrl()}${u}`;
  return undefined;
};

//Ordering Media row 
const isVideoLike = (item: any): boolean => {
  if (!item) return false;
  if (item.type === 'video') return true;
  if (item.isHls && item.videoId) return true;
  if (item.file?.type?.startsWith('video/')) return true;
  if (typeof item.url === 'string' && /\.(mp4|webm|mov|m4v)$/i.test(item.url)) return true;
  if (typeof item.hlsUrl === 'string' && item.hlsUrl.length > 0) return true;
  return false;
};

const orderSingleVideoFirst = <T,>(items: T[], isVideo: (item: T) => boolean): T[] => {
  const video = items.find(isVideo);
  const rest = items.filter(item => !isVideo(item));
  return video ? [video, ...rest] : rest;
};

const hasAnyVideoInComposer = () => {
  return [...existingMedia, ...mediaItems].some(isVideoLike);
};


//Use Effects
useEffect(() => {
  const fetchPostsLeft = async () => {
    if (!user) {
      setPostsLeft(DAILY_POST_LIMIT);
      return;
    }

    if (!token) {
      setPostsLeft(DAILY_POST_LIMIT);
      return;
    }

    try {
      setPostsLeftLoading(true);
      const { data } = await api.get('/media/my-posts');

      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const todayCount = (Array.isArray(data) ? data : []).filter((p: any) => {
        if (!p?.createdAt) return false;
        const created = new Date(p.createdAt);
        return created >= startOfDay;
      }).length;

      const remaining = Math.max(DAILY_POST_LIMIT - todayCount, 0);
      setPostsLeft(remaining);
    } catch (err) {
      console.error('Failed to fetch daily post count:', err);
      // Fallback: assume full allowance if error
      setPostsLeft(DAILY_POST_LIMIT);
    } finally {
      setPostsLeftLoading(false);
    }
  };

  fetchPostsLeft();
}, [user, token]);


useEffect(() => {
  if (!sidebarData || sidebarInitialized.current) return;

  if (sidebarData.hashtags) {
    const limited = sidebarData.hashtags.slice(0, MAX_HASHTAGS); // 👈 clamp
    setSelectedHashtagList(limited);
    setUserEnteredHashtags(limited.join(' '));
  }

  sidebarInitialized.current = true; // ✅ only apply once
}, [sidebarData]);


useEffect(() => {
  if (!isPreview || !previewFilename || !previewFile) return;

  const previewId = editTargetId || `preview-${Date.now()}`;
  if (hasInjectedRef.current === previewFilename) return;

  const objectURL = URL.createObjectURL(previewFile);

  const existing = mediaItems.find(m => m.id === previewId);

  addOrReplaceMediaItem({
    id: previewId,
    file: previewFile,
    url: existing?.url,
    musicTitle: existing?.musicTitle ?? null,
    musicArtist: existing?.musicArtist ?? null,
    filterId: existing?.filterId ?? null, 
  });

  setObjectURLs((prev) => ({
    ...prev,
    [previewId]: objectURL,
  }));

  hasInjectedRef.current = previewFilename;

// ⛔️ no cleanup that revokes objectURL here
}, [isPreview, previewFilename, previewFile, editTargetId, mediaItems, addOrReplaceMediaItem]);

  const { postId } = location.state || {};
  const [existingMedia, setExistingMedia] = useState<ExtMediaItem[]>([]);
  const [objectURLs, setObjectURLs] = useState<Record<string, string>>({});
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [selectedHashtagList, setSelectedHashtagList] = useState<string[]>([]);

  const totalMediaCount = existingMedia.length + mediaItems.length;

  const canUseGeo = async () => {
    if (!('permissions' in navigator)) return true; // older browsers
    try {
      // @ts-ignore - older TS lib doms may not include 'geolocation'
      const status = await navigator.permissions.query({ name: 'geolocation' });
      return status.state !== 'denied'; // 'granted' or 'prompt' are OK
    } catch {
      return true;
    }
  };
  
  // Read video duration from a File (in ms), or null if unknown
const getVideoDurationMsFromFile = (file: File): Promise<number | null> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('video/')) {
      resolve(null);
      return;
    }

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const dur = video.duration; // seconds
      if (!isFinite(dur) || dur <= 0) {
        resolve(null);
      } else {
        resolve(Math.round(dur * 1000)); // ms
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };

    video.src = url;
  });
};

const [activeVideoChipId, setActiveVideoChipId] = useState<string | null>(null);
const chipHideTimeoutRef = useRef<number | null>(null);

const showMusicChipFor = (id: string) => {
  setActiveVideoChipId(id);

  if (chipHideTimeoutRef.current) {
    window.clearTimeout(chipHideTimeoutRef.current);
  }

  chipHideTimeoutRef.current = window.setTimeout(() => {
    setActiveVideoChipId(prev => (prev === id ? null : prev));
    chipHideTimeoutRef.current = null;
  }, 2500); // ≈ when native controls fade
};

useEffect(() => {
  return () => {
    if (chipHideTimeoutRef.current) {
      window.clearTimeout(chipHideTimeoutRef.current);
    }
  };
}, []);

  const fetchLocationForPosting = async (): Promise<PostLocation & { countryCode?: string }> => {
    if (!prefs?.locationConsent) {
      navigate(`/require-privacy-change?return=${encodeURIComponent(currentPath)}`, { replace: true });
      throw new Error('Location consent required');
    }
  
    // 👇 quick deny check — avoids waiting for getCurrentPosition
    const ok = await canUseGeo();
    if (!ok) {
      navigate(`/require-privacy-change?return=${encodeURIComponent(currentPath)}`, { replace: true });
      throw new Error('Browser geolocation denied');
    }
  
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
      });
      const { latitude, longitude } = position.coords;
      const lang = navigator.language || 'en';
  
      const res = await api.get('/media/reverse-geocode', {
        params: { lat: latitude, lng: longitude, lang },
      });
  
      const { city, country, countryCode } = res.data || {};
      return {
        city: city || UNKNOWN,
        country: country || UNKNOWN,
        countryCode,
      };
    } catch {
      return { city: UNKNOWN, country: UNKNOWN };
    }
  };
  
  const mediaLabel = (m?: Pick<MediaItem, 'musicTitle' | 'musicArtist'>) =>
    [m?.musicTitle, m?.musicArtist].filter(Boolean).join(' — ');
  
  
  
  // Load privacy preferences
  useEffect(() => {
      (async () => {
        try {
          const { data } = await api.get('/privacy/preferences');
          setPrefs({
            analyticsConsent: !!data?.analyticsConsent,
            pushConsent: !!data?.pushConsent,
            locationConsent: !!data?.locationConsent,
          });
        } catch {
          setPrefs({ analyticsConsent: false, pushConsent: false, locationConsent: false });
        } finally {
          setPrefsLoading(false);
        }
      })();
    }, []);
  

 //Handle file selection
 // Put this near the top-level of the component (once)
// Put this near the top-level of the component (once)
const uid = () =>
  (globalThis.crypto?.randomUUID?.() ??
    `id-${Date.now()}-${Math.random().toString(36).slice(2)}`);

// PICK FILES
const processPickedFiles = async (picked: File[]) => {
  if (picked.length === 0) return;

  const existingCount = existingMedia.length + mediaItems.length;
  let remainingSlots = Math.max(0, MAX_MEDIA_PER_POST - existingCount);

  if (remainingSlots <= 0) {
    alert(t('galleryPosting.alerts.mediaPerPostLimit'));
    return;
  }

  setIsTranscoding(true);

  const newItems: MediaItem[] = [];
  const newUrls: Record<string, string> = {};

  const existingNames = new Set(
    mediaItems.map(m => m.file?.name).filter(Boolean) as string[]
  );

  let skippedByDuration = 0;
  let skippedByCap = 0;
  let skippedByVideoRule = 0;

  let videoAlreadyExists = hasAnyVideoInComposer();
  let acceptedNewVideo = false;

  for (const file of picked) {
  if (remainingSlots <= 0) {
    skippedByCap++;
    continue;
  }

  if (existingNames.has(file.name)) continue;

  const isVideoFile = file.type.startsWith('video/');

  // only one video total in the whole composer
  if (isVideoFile && (videoAlreadyExists || acceptedNewVideo)) {
    skippedByVideoRule++;
    continue;
  }

  if (isVideoFile) {
    const durMs = await getVideoDurationMsFromFile(file);

    if (durMs != null && durMs < MIN_VIDEO_DURATION_MS) {
      skippedByDuration++;
      continue;
    }

    if (durMs != null && durMs > MAX_VIDEO_DURATION_MS) {
      skippedByDuration++;
      continue;
    }
  }

  const id = `new-${uid()}`;
  newItems.push({ id, file } as MediaItem);
  newUrls[id] = URL.createObjectURL(file);
  remainingSlots--;

  if (isVideoFile) {
    acceptedNewVideo = true;
  }
}

if (newItems.length === 0) {
  setIsTranscoding(false);

  if (skippedByDuration > 0) {
    alert(
      t(
        'galleryPosting.alerts.videosSkippedInvalidDuration',
        'Some videos were skipped because they must be between 2.9 seconds and 183 seconds.'
      )
    );
  }
  if (skippedByVideoRule > 0) {
    alert(t('galleryPosting.alerts.onlyOneVideoAllowed', 'Only one video is allowed per post.'));
  }
  if (skippedByCap > 0) {
    alert(t('galleryPosting.alerts.mediaPerPostHardLimit'));
  }

  return;
}

  // If a new video was accepted, force it to the first position of mediaItems
  const orderedNewItems = orderSingleVideoFirst(newItems, (item) =>
    Boolean(item.file?.type?.startsWith('video/'))
  );

  setMediaItems(prev => {
    const combined = [...prev, ...orderedNewItems];
    return orderSingleVideoFirst(combined, isVideoLike);
  });

  setObjectURLs(prev => ({ ...prev, ...newUrls }));
  setIsTranscoding(false);

  if (skippedByDuration > 0) {
    alert(t('galleryPosting.alerts.videosSkippedTooLongSummary'));
  }
  if (skippedByVideoRule > 0) {
    alert(t('galleryPosting.alerts.onlyOneVideoAllowed', 'Only one video is allowed per post.'));
  }
  if (skippedByCap > 0) {
    alert(t('galleryPosting.alerts.filesSkippedByCapSummary'));
  }
};


// ⬇️ existing input handler now just delegates
const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const picked = Array.from(e.target.files || []);
  await processPickedFiles(picked);
};

// ⬇️ NEW: drop handler (used by the drop zone)
const handleDropFiles = async (e: React.DragEvent<HTMLDivElement>) => {
  e.preventDefault();
  e.stopPropagation();
  setIsDragOver(false);

  if (isTranscoding) return;

  const picked = Array.from(e.dataTransfer.files || []);
  await processPickedFiles(picked);
};



  const handleDelete = (id: string) => {
    const updated = mediaItems.filter(item => item.id !== id);
    setMediaItems(updated);
  
    if (objectURLs[id]) {
      URL.revokeObjectURL(objectURLs[id]);
      setObjectURLs((prev) => {
        const updatedURLs = { ...prev };
        delete updatedURLs[id];
        return updatedURLs;
      });
    }
    };

    const handleOpenMusic = async (entry: {
      musicId?: string | null;
      musicTitle?: string | null;
      musicArtist?: string | null;
    }) => {
      // 1️⃣ Best case: we already know the track id
      if (entry.musicId) {
        navigate(`/musicdetails/${entry.musicId}`);
        return;
      }
    
      // 2️⃣ Fallback: search by EITHER title or artist (not "title artist")
      const q = (entry.musicTitle || entry.musicArtist || '').trim();
      if (!q) {
        return;
      }
    
      try {
        const res = await api.get('/music', { params: { q } });
        const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);
    
        if (!list.length) {
          return;
        }
    
        navigate(`/musicdetails/${list[0]._id}`);
      } catch (e) {
        console.error('[GalleryPosting.handleOpenMusic] Music lookup failed:', e);
      }
    };
    
    
//Navigation hard loading to Gallery after upload
const GALLERY_HARD_RELOAD_KEY = 'gallery:hardReload';

const goToGalleryHard = (replace = true) => {
  sessionStorage.setItem(GALLERY_HARD_RELOAD_KEY, '1');
  const url = `/gallery?refresh=${Date.now()}`;
  navigate(url, { replace });
};

//Posting to Gallery

  const postingRef = React.useRef(false);

  const handlePostToBlog = async () => {
    if (postingRef.current) {
      console.warn("🚧 Post action already in progress. Aborting...");
      return;
    } 
    if (!prefs || prefsLoading) {
            // Wait until we know; soft-block until prefs are loaded
            return;
          }
          if (!prefs.locationConsent) {
            navigate(`/require-privacy-change?return=${encodeURIComponent(currentPath)}`, { replace: true });
            return;
          }
    postingRef.current = true;
    setIsLoading(true); // Show loading overlay 
    try {
      if (!token) {
        alert(t('galleryPosting.alerts.loginRequired'));
        postingRef.current = false;
        setIsLoading(false); // Hide loading overlay
        return;
      }
  
      let detectedLocation: PostLocation = {
        city: UNKNOWN,
        country: UNKNOWN,
      };
   
      try {
        detectedLocation = await fetchLocationForPosting();
      } catch (err) {
      }
      
  
      await proceedWithPost(detectedLocation);
  
    } catch (err) {
      console.error("❌ Error in handlePostToBlog:", err instanceof Error ? err.message : err);
    } finally {
      postingRef.current = false;
      setIsLoading(false); // Hide loading overlay
    }
  }; 
  const proceedWithPost = async (place: PostLocation) => {
    try {
      if (!token) return;
  
      const itemsWithFiles = mediaItems.filter(mi => mi.file);
  
      const processedFiles = await Promise.all(
        itemsWithFiles.map(async mi => {
          const before = mi.file!;
          const after = await prepareFileForUpload(before);
          return after;
        })
      );
  
      const totalBytes = processedFiles.reduce(
        (sum, f) => sum + (f?.size || 0),
        0
      );
  
      if (totalBytes > MAX_POST_MEDIA_BYTES) {
        const actualGb = (totalBytes / (1024 * 1024 * 1024)).toFixed(2);
        const maxGb = (MAX_POST_MEDIA_BYTES / (1024 * 1024 * 1024)).toFixed(2);
        alert(
          t('galleryPosting.alerts.postTooLargeClient', {
            actualGb,
            maxGb,
          })
        );
        return;
      }
  
      const looksGenericCameraName = (base: string) => {
        const b = base.toLowerCase();
        return (
          b === 'media-0' || b === 'media-1' || b === 'media' ||
          b === 'video-0' || b === 'video' ||
          /^img[_-]?\d+$/i.test(base) ||
          /^vid[_-]?\d+$/i.test(base) ||
          /^pxl[_-]?\d+/i.test(base) ||
          base.length < 4
        );
      };
  
      const typePrefix = (f: File) =>
        f.type.startsWith('video/')
          ? 'video'
          : f.type.startsWith('image/')
          ? 'image'
          : 'file';
  
      const normalizeBaseFromFile = (f: File, fallbackId: string) => {
        const rawBase = (f.name || '').replace(/\.[^.]+$/, '');
        const safeRaw = rawBase.replace(/[^A-Za-z0-9._-]/g, '');
        if (!safeRaw || looksGenericCameraName(safeRaw)) {
          const stamp = new Date().toISOString().replace(/[:.]/g, '');
          return `${typePrefix(f)}_${stamp}`;
        }
        return safeRaw;
      };
  
      const extOf = (f: File) =>
        (f.name.match(/\.[a-z0-9]+$/i)?.[0]?.toLowerCase()) ||
        (f.type.startsWith('video/') ? '.mp4'
          : f.type.startsWith('image/') ? '.jpg'
          : '.bin');
  
      // ⬆️ Setup done — now handle uploads
      const r2Uploads: R2UploadResult[] = [];

// 1️⃣ First upload all VIDEOS (one by one)
// 1️⃣ First upload all VIDEOS (one by one)
for (let i = 0; i < processedFiles.length; i++) {
  const f = processedFiles[i];
  const srcItem = itemsWithFiles[i];

  if (!f.type.startsWith("video/")) {
    r2Uploads[i] = undefined as any;
    continue;
  }

  const ext = extOf(f);
  const base = normalizeBaseFromFile(f, srcItem.id);
  const stamp = new Date().toISOString().replace(/[:.]/g, "");
  const uniqueName = `${base}_${stamp}${ext}`;

  const fileWithUniqueName = new File([f], uniqueName, {
    type: f.type || "application/octet-stream",
  });

  console.log("🎬 Uploading video:", uniqueName);

  const upload = await uploadViaBackendHls(fileWithUniqueName);

  r2Uploads[i] = {
    videoId: upload.videoId,
    isHls: true,
    hlsUrl: upload.hlsUrl ?? null,
    dashUrl: upload.dashUrl ?? null,
    posterUrl: upload.posterUrl ?? null,
  };
}

// 2️⃣ Upload all IMAGES in parallel
const imagePromises = processedFiles.map(async (f, i) => {
  if (f.type.startsWith("video/")) return;

 const stamp = new Date().toISOString().replace(/[:.]/g, "");
const uniqueName = `${Date.now()}-image_${stamp}_${stamp}.jpeg`;

const fileWithUniqueName = new File([f], uniqueName, {
  type: "image/jpeg",
});

  const form = new FormData();
  form.append("file", fileWithUniqueName);

  const res = await api.post("/mediaImg/upload-image", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  r2Uploads[i] = { url: res.data.url };
});

await Promise.all(imagePromises);

      
      const uploadedUrlById = new Map<string, string>();

r2Uploads.forEach((u, i) => {
  const id = itemsWithFiles[i].id;
  if ("url" in u) {
    uploadedUrlById.set(id, u.url);
  }
});

      const uploadedVideoIdByItemId = new Map<string, string>();

r2Uploads.forEach((u, i) => {
  const id = itemsWithFiles[i].id;
  if ("videoId" in u) {
    uploadedVideoIdByItemId.set(id, u.videoId);
  }
});

  
      const metaFor = (id: string) => {
        const found = mediaItems.find(mi => mi.id === id);
        return {
          musicTitle: found?.musicTitle ?? null,
          musicArtist: found?.musicArtist ?? null,
          musicId: (found as any)?.musicId ?? null,
          filterId: found?.filterId ?? null,
        };
      };
  
      const existingEntries = existingMedia.map(m => {
        // 🎬 HLS
       if (m.isHls && m.videoId) {
  return {
    videoId: m.videoId,
    isHls: true,
    hlsUrl: m.hlsUrl ?? m.url ?? null,
    dashUrl: m.dashUrl ?? null,
    posterUrl: m.posterUrl ?? m.thumbnailUrl ?? null,
    type: "video",
    sizeBytes: null,
    musicTitle: m.musicTitle ?? null,
    musicArtist: m.musicArtist ?? null,
    musicId: m.musicId ?? null,
    filterId: m.filterId ?? null,
  };
}
      
        // 🖼 Image
        return {
          url: m.url!,
          type: "image",
          musicTitle: m.musicTitle ?? null,
          musicArtist: m.musicArtist ?? null,
          musicId: m.musicId ?? null,
          filterId: m.filterId ?? null,
        };
      });
      

      const uploadedEntries = itemsWithFiles.map((mi, idx) => {
  const file = processedFiles[idx];
  const isVideo = file?.type?.startsWith("video/");

  if (isVideo) {
    const videoId = uploadedVideoIdByItemId.get(mi.id);
    if (!videoId) {
      throw new Error(`Missing videoId for ${mi.id}`);
    }

    const upload = r2Uploads[idx] as Extract<R2UploadResult, { videoId: string }>;

return {
  videoId: upload.videoId,
  isHls: true,
  hlsUrl: upload.hlsUrl ?? null,
  dashUrl: upload.dashUrl ?? null,
  posterUrl: upload.posterUrl ?? null,
  type: "video",
  sizeBytes: file?.size ?? null,
  ...metaFor(mi.id),
};
  }

  // 🖼 IMAGE
  return {
    url: uploadedUrlById.get(mi.id)!,
    type: "image",
    sizeBytes: file?.size ?? null,
    mime: file?.type ?? null,
    isHls: false,
    ...metaFor(mi.id),
  };
});

        
  
      const mediaEntries = orderSingleVideoFirst(
         [...existingEntries, ...uploadedEntries],
         isVideoLike
);
      const limitedHashtags = selectedHashtagList.slice(0, MAX_HASHTAGS);
  
      const videoCount = mediaEntries.filter(isVideoLike).length;

if (videoCount > 1) {
  alert(t('galleryPosting.alerts.onlyOneVideoAllowed', 'Only one video is allowed per post.'));
  return;
}

if (videoCount === 1 && !isVideoLike(mediaEntries[0])) {
  alert(t('galleryPosting.alerts.videoMustBeFirst', 'Video must be the first media item.'));
  return;
}

      const mediaPost = {
        media: mediaEntries,
        location: place,
        hashtags: limitedHashtags.join(' '),
      };
  
      const postRes = await api.post('/media', mediaPost);
      console.log('✅ Media post created:', postRes.data);
  
      goToGalleryHard();
    } catch (err: any) {
      console.error('❌ Failed to upload media:', err);
  
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
  
      if (code === 'POST_TOO_LARGE') {
        const maxGb = (MAX_POST_MEDIA_BYTES / (1024 * 1024 * 1024)).toFixed(2);
        alert(
          t('galleryPosting.alerts.postTooLargeServer', {
            maxGb,
          })
        );
      } else if (code === 'DAILY_POST_LIMIT_REACHED') {
        alert(t('galleryPosting.alerts.dailyPostLimitReached'));
        setPostsLeft(0);
      } else if (status === 403 && code === 'VPN_BLOCKED') {
        const msg =
          err.response?.data?.msg ||
          t('galleryPosting.alerts.vpnBlockedFallback');
        alert(msg);
        setVpnBlockedMessage(msg);
      } else {
        alert(t('galleryPosting.alerts.postFailed'));
      }
    }
  };
  
  

  // Cancel: go back if possible; otherwise fall back to Gallery
const handleCancel = () => {
  setCancelConfirmOpen(true);
};

const confirmCancel = () => {
  setCancelBusy(true);

  if (window.history && window.history.length > 1) {
    navigate('/gallery');
  } else {
    navigate('/gallery', { replace: true });
  }
};

  const shouldReset = location.state?.reset === true;
  const hasReset = useRef(false);

  useEffect(() => {
  if (shouldReset && !hasReset.current) {
    hasReset.current = true;

    setMediaItems([]);
    setExistingMedia([]);
    setUserEnteredHashtags('');
    setSelectedHashtagList([]);
    setObjectURLs({});
  }
}, [shouldReset, setMediaItems]);



// Playable source: prefer local blob URL during edit; else remote http(s)



// When building src, avoid returning "" (empty string can cause odd fetches)
const srcFor = (item: any): string | undefined => {
  const managed = objectURLs[item.id];
  if (managed) return managed;

  if (item.isHls) {
    return item.hlsUrl || item.url || (item.videoId
      ? `${getApiBaseUrl()}/hls/playlist/${item.videoId}`
      : undefined);
  }

  if (item.url?.startsWith("blob:")) return undefined;
  if (/^https?:\/\//i.test(item.url)) return item.url;
  if (item.url?.startsWith("/")) return `${getApiBaseUrl()}${item.url}`;

  return undefined;
};

// Keep a ref mirror to avoid using objectURLs in the effect dependency
const objectURLsRef = React.useRef(objectURLs);
React.useEffect(() => { objectURLsRef.current = objectURLs; }, [objectURLs]);

useEffect(() => {
  const toCreate: Record<string, string> = {};
  const existing = objectURLsRef.current;

  for (const item of mediaItems) {
    if (!item.file) continue;

    const isPreviewVideo =
      item.file.type.startsWith('video/') && item.url?.startsWith('/uploadsPreview/');
    const needsObjectUrl = !item.url || isPreviewVideo;

    if (!needsObjectUrl || existing[item.id] || toCreate[item.id]) continue;

    toCreate[item.id] = URL.createObjectURL(item.file);
  }

  if (Object.keys(toCreate).length) {
    setObjectURLs(prev => ({ ...prev, ...toCreate }));
  }
}, [mediaItems]); // 👈 not depending on objectURLs anymore


// Single fetch effect
useEffect(() => {
  const fetchMediaData = async () => {
    if (!postId) return;

    try {
      const res = await api.get(`/events/${postId}`);
      const rawList: any[] = Array.isArray(res.data?.media) ? res.data.media : [];

      const fetchedMedia = rawList
      .map(fromServerMedia)
      .filter(m => m.url || (m.isHls && m.videoId));

      const dropped = rawList.length - fetchedMedia.length;
      if (dropped > 0) {
        console.warn(`[GalleryPosting] Dropped ${dropped} server media with invalid blob: URLs`);
      }

      setExistingMedia(fetchedMedia);
    } catch (err) {
      console.error("Failed to fetch media data:", err);
    }
  };

  fetchMediaData();
}, [postId]);



useEffect(() => {
  return () => {
    Object.values(objectURLsRef.current).forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {}
    });
  };
}, []);


  //For reset
  useEffect(() => {
    if (shouldReset && !hasReset.current) {
      hasReset.current = true;
      setUserEnteredHashtags('');
      setSelectedHashtagList([]);
      setObjectURLs({});
    }
  }, [shouldReset]);
  
  
  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-hide-scrollbars', 'true');
    style.innerHTML = `
      .hScroll {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      .hScroll::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);


  const LoadingOverlay = () => (
    <div style={loadingOverlayStyle}>
      <div style={loadingContentStyle}>
      <span>{t('galleryPosting.overlay.uploading')}</span>
        <div style={loadingDotsContainer}>
          <span style={loadingDot}>.</span>
          <span style={loadingDot}>.</span>
          <span style={loadingDot}>.</span>
        </div>
      </div>
    </div>
  );

  
  return (
    <>
      {isLoading && <LoadingOverlay />}

      <div style={pageShellStyle}>
        {/* HEADER – same visual family as Home / auth pages */}
        <header style={pageHeaderStyle}>
  <div style={pageHeaderLeft}>
    <HighLogo
      src={logo}
      style={{ width: '12.2%', display: 'block' }}
      padding={0}
      onClick={() => navigate('/gallery')}
    />
  </div>
</header> 

        {/* MAIN CONTENT */}
        <main style={pageMainStyle}>
          {/* 🔔 VPN warning banner (if any) */}
          {vpnBlockedMessage && (
            <div style={vpnBannerStyle}>
              {vpnBlockedMessage}
            </div>
          )}

          {/* Main card with sidebar + media area */}
          <div style={cardGridStyle}>
            {/* LEFT: sidebar card */}
            <aside style={sidebarContainerStyle}>
              <div
    style={{
      top: 76,
    }}
  >
              <SidebarGalleryPosting
                selectedHashtags={selectedHashtagList}
                onHashtagChange={(tags: string[]) => {
                  let next = tags;

                  if (tags.length > MAX_HASHTAGS) {
                    next = tags.slice(0, MAX_HASHTAGS);
                    alert(
                      t('galleryPosting.alerts.hashtagLimit', {
                        max: MAX_HASHTAGS,
                      })
                    );
                  }

                  setSelectedHashtagList(next);
                  setUserEnteredHashtags(next.join(' '));
                }}
                onCancel={handleCancel}
                onPost={handlePostToBlog}
                isPostDisabled={
                  isLoading ||
                  postsLeft === 0 ||
                  (mediaItems.length === 0 && existingMedia.length === 0)
                }
                postsLeft={postsLeft ?? DAILY_POST_LIMIT}
              />
              </div>
            </aside>


{/* Left bottom terms privacy help buttons */}
<div
  style={{
    position: "fixed",
    left: 18,
    bottom: 8,
    width: 230,
    zIndex: 50,
    fontSize: 10,
    color: "#555",
    lineHeight: 1.6,
    textAlign: "left",
    pointerEvents: "auto",
  }}
>
  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/helpsupportstart")}
  >
    {t("mainSidebar.footer.helpSupport")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/privacypolicy")}
  >
    {t("mainSidebar.footer.privacyPolicy")}
  </span>

  {" · "}

  <span
    style={{ cursor: "pointer" }}
    onClick={() => navigate("/legal/termsofuse")}
  >
    {t("mainSidebar.footer.termsOfUse")}
  </span>
</div>

            {/* RIGHT: media composer card */}
            <section style={mediaOuterStyle}>
              <div style={postCardStyle}>
                {/* 👤 Header with Profile */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 0,
                    marginLeft: '15px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        width: 35,
                        height: 35,
                        borderRadius: '5%',
                        marginRight: 10,
                        backgroundColor: user?.picture
                          ? 'transparent'
                          : 'rgb(38, 29, 162)',
                        backgroundImage: user?.picture
                          ? `url(${user.picture})`
                          : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: user?.picture ? 0 : '20px',
                      }}
                    >
                      {!user?.picture && '👻'}
                    </div>
                    <strong>
                      {user?.profilename || t('galleryPosting.fallback.anonymous')}
                    </strong>
                  </div>
                </div>

                {/* ✅ Scrollable Media Section */}
                <div style={mediaScrollerStyle}
                  className="hScroll"
>

  {orderSingleVideoFirst(
  [...existingMedia, ...mediaItems] as ExtMediaItem[],
  isVideoLike
).map((item, index) => {

  const fid = getFilterId(item.id) ?? item.filterId ?? null;
  const overlay = overlayStyleFor(fid);
  const mediaUrl = item.isHls && item.videoId ? null : srcFor(item);

  const resolvedSrc =
  item.isHls
    ? (item.hlsUrl || item.url || (item.videoId
        ? `${getApiBaseUrl()}/hls/playlist/${item.videoId}`
        : undefined))
    : srcFor(item);

if (!resolvedSrc) return null;


  return (
    <div
      key={item.id}
      style={{
        flex: '0 0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >

      {/* ================= VIDEO ================= */}
      {isVideoItem(item) ? (
        <div
  onMouseMove={() => showMusicChipFor(item.id)}
  onMouseLeave={() => setActiveVideoChipId(null)}
  style={{
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
  }}
>

          {/* ✅ VIDEO */}
<VideoPlayerWeb
  src={resolvedSrc}
  poster={item.posterUrl || item.thumbnailUrl || undefined}
  onOrientation={(land) =>
    setOrientationMap(prev => ({ ...prev, [item.id]: land }))
  }
  style={{
    width: 'auto',
    height: 'auto',
    maxHeight: orientationMap[item.id] ? undefined : '81vh',
    maxWidth: orientationMap[item.id] ? '81vh' : undefined,
    borderRadius: 12,
  }}
/>


{/* 🎵 MUSIC CHIP */}
{!!(item.musicTitle || item.musicArtist) && (
  <div
    style={{
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      transform: 'translateY(100%)', // ✅ same as EditPosting
      padding: '6px 10px 10px',
      fontSize: 12,
      color: '#666',
      background:
        'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.6) 100%)',
      border: 'none',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      pointerEvents: 'auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
    }}
    title={mediaLabel(item)}
  >
    <span aria-hidden="true">🎵</span>

    {item.musicId ? (
      <Link
        to={`/musicdetails/${item.musicId}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          color: '#92979f',
          fontWeight: 600,
          textDecoration: 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {mediaLabel(item)}
      </Link>
    ) : (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleOpenMusic(item);
        }}
        style={{
          background: 'transparent',
          border: 0,
          padding: 0,
          color: '#c1c9d6',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {mediaLabel(item)}
      </button>
    )}
  </div>
)}


          {overlay && (
            <div
              style={{
                ...overlay,
                borderRadius: 12,
                position: 'absolute',
                inset: 0,
              }}
            />
          )}
        </div>

      ) : (
      /* ================= IMAGE ================= */
        <div
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
  <img
  src={mediaUrl ?? undefined}
  alt={`media-${index}`}
  loading="lazy"
  onLoad={(e) => {
    const img = e.currentTarget;
    const isLandscape = img.naturalWidth > img.naturalHeight;
    setOrientationMap(prev => ({ ...prev, [item.id]: isLandscape }));
  }}
  style={{
  width: 'auto',
  height: 'auto',

  maxHeight: orientationMap[item.id] ? undefined : '81vh',
  maxWidth: orientationMap[item.id] ? '81vh' : undefined,

  borderRadius: 12,
}}
/>

          {overlay && (
            <div
              style={{
                ...overlay,
                borderRadius: 12,
                position: 'absolute',
                inset: 0,
              }}
            />
          )}
        </div>
      )}

      {/* ================= ACTION BUTTONS ================= */}
      <div
        style={{
          width: '90%',
          display: 'flex',
          gap: 10,
          justifyContent: 'center',
        }}
      >
       {isVideoItem(item) && (
  <button
    onClick={() => {
      navigate('/add-music', {
        state: {
          editTargetId: item.id,
          sidebarData: {
            hashtags: selectedHashtagList,
          },
remoteUrl: item.isHls
  ? (item.hlsUrl || item.url || (item.videoId
      ? `${getApiBaseUrl()}/hls/playlist/${item.videoId}`
      : undefined))
  : srcFor(item),
        },
      });
    }}
    style={{
      padding: '4px 8px',
      borderRadius: 999,
      color: '#cbd5f5',
      cursor: 'pointer',
      fontSize: 12,
       fontWeight: 500,
       marginTop: 32,
      background: '#2563eb',
      border: 'none',
    }}
  >
    🎵 {item.musicId ? t('galleryPosting.buttons.changeMusic', 'Change music') : t('galleryPosting.buttons.addMusic', 'Add music')}
  </button> 
)}

        <button
          onClick={() => handleDelete(item.id)}
          style={smallActionButton}
        >
          {t('galleryPosting.buttons.delete')}
        </button>


        <button
  onClick={(e) => {
    e.stopPropagation();

    // ensure latest filterId is persisted
    addOrReplaceMediaItem({
      id: item.id,
      url: item.url,
      file: item.file,
      musicTitle: item.musicTitle ?? null,
      musicArtist: item.musicArtist ?? null,
      filterId: getFilterId(item.id),
    });

    const remoteUrl = toRemoteHttp(item);

    if (isVideoItem(item)) {
      navigate(`/editor/video/${item.id}`, {
        state: {
          editTargetId: item.id,
          mediaItem: item,
          remoteUrl,
          file: item.file ?? null, 
          sidebarData: {
            hashtags: selectedHashtagList,
          },
        },
      });
    } else {
      navigate(`/editor/image/${item.id}`, {
        state: {
          mediaItem: item,
          sidebarData: {
            hashtags: selectedHashtagList,
          },
        },
      });
    }
  }}
  style={smallActionButton}
>
  {t('galleryPosting.buttons.filter')}
</button>

      </div>
    </div>
  );
})}


                  {/* ➕ Add Media tile */}
                  {totalMediaCount < MAX_MEDIA_PER_POST && (
                    <div
                      style={
                        totalMediaCount === 0
                          ? addMediaCardFullWidthStyle
                          : addMediaCardCompactStyle
                      }
                      onDragOver={(e) => {
                        if (!e.dataTransfer.types.includes('Files')) return;
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget === e.target) {
                          setIsDragOver(false);
                        }
                      }}
                      onDrop={handleDropFiles}
                    >
                      {isTranscoding ? (
                        <div className="spinner" />
                      ) : (
                        <label
                          style={{
                            cursor: 'pointer',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 32, marginBottom: 6 }}>
                            📷 🎬
                          </div>
                          <div
                            style={{
                              fontSize: 14,
                              color: '#e5e7eb',
                              marginBottom: 4,
                            }}
                          >
                            {t("galleryPosting.addMedia.title", "Click here to choose Images & Videos")}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: '#9ca3af',
                            }}
                          >
                           {t("galleryPosting.addMedia.subtitle", "…or drop them here")}
                          </div>

                          <input
                            type="file"
                            accept="image/*, video/*"
                            multiple
                            onChange={handleFileChange}
                            disabled={isTranscoding}
                            style={{ display: 'none' }}
                          />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </main>
        {cancelConfirmOpen && (
  <div style={confirmOverlay}>
    <div style={confirmCard} onClick={(e) => e.stopPropagation()}>
      <div style={{ marginBottom: 14, fontSize: 16, fontWeight: 600 }}>
        {t('galleryPosting.alerts.discardConfirm')}
      </div>

      <button
        onClick={confirmCancel}
        disabled={cancelBusy}
        style={primaryBtn}
      >
        {t('common.confirm')}
      </button>

      <button
        onClick={() => setCancelConfirmOpen(false)}
        disabled={cancelBusy}
        style={cancelBtn}
      >
        {t('common.cancel')}
      </button>
    </div>
  </div>
)}

      </div>
    </>
  );

};

/* 🌌 Page shell */
const pageShellStyle: React.CSSProperties = {
  paddingTop: 25,  
  background:
    'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
  color: '#f9fafb',
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

/* 🧭 Header */
const pageHeaderStyle: React.CSSProperties = {
  position: 'absolute',
  top: '21px',
  left: '-8px',
  right: 0,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',    
  background: 'transparent',
};

const pageHeaderLeft: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
};


/* 🧱 Main */
const pageMainStyle: React.CSSProperties = {
  flex: 1,
  padding: '0px 10px 0 24px',
};

/* VPN banner */
const vpnBannerStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 10,
  backgroundColor: '#3b0d0d',
  color: '#ffcccc',
  fontSize: 14,
  marginBottom: 16,
};

/* Grid: sidebar + composer */
const cardGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '300px minmax(0, 1fr)',
  gap: 0,
  alignItems: 'start',
  marginLeft: '-18px', 
};



/* Sidebar container */
const sidebarContainerStyle: React.CSSProperties = {
  position: 'sticky',
  overflow: 'visible', 
  marginTop: "50px",
};

/* Composer area outer */
const mediaOuterStyle: React.CSSProperties = {
  width: '100%',
  alignSelf: 'start',
};   

/* Composer card */
const postCardStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  background: 'transparent',
  padding: 0,
  borderRadius: 0,
  border: 'none',
  boxShadow: 'none',
  color: '#e5e7eb',
  display: 'flex',
  flexDirection: 'column',
  marginLeft: '-15px',  // Move the entire card to the left by 10px
};

/* Horizontal media scroller */
const mediaScrollerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: 40,
  width: '100%',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  paddingTop: 15,
  paddingLeft: '15px',  // Shift content 10px to the right (create buffer zone)
};


/* Small action buttons under each media */
const smallActionButton: React.CSSProperties = {
  padding: '4px 8px',
  backgroundColor: '#020617',
  border: '1px solid #cbd5f5',
  borderRadius: 999,
  color: '#cbd5f5',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  marginTop: 32,
};


const loadingOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const loadingContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontSize: '24px',
  color: '#380675',
};

const loadingDotsContainer: React.CSSProperties = {
  display: 'flex',
  gap: '5px',

};

const loadingDot: React.CSSProperties = {
  animation: 'loadingDots 1s infinite',
  fontSize: '36px',
};


/* ➕ Add media tile – base */
const addMediaBaseStyle: React.CSSProperties = {
  justifyContent: 'center',
  display: 'flex',
  alignItems: 'center',
  border: '2px dashed rgba(148,163,184,0.6)',
  borderRadius: 20,
  background:
    'radial-gradient(circle at top, #020617, #020617 40%, #000 100%)',
};

/* Empty state → fills the container more */
const addMediaCardFullWidthStyle: React.CSSProperties = {
  ...addMediaBaseStyle,
  flex: '1 1 auto',
  maxWidth: 'min(1000px, 100%)',
  height: 440,
  maxHeight: 480,
  marginLeft: 0,
  marginTop: 10,
};

/* Non-empty state → compact tile to the right */
const addMediaCardCompactStyle: React.CSSProperties = {
  ...addMediaBaseStyle,
  flex: '0 0 auto',
  width: 420,
  height: 420,
  maxHeight: 500,
  marginTop: 5,

};

const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.85)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 99999999,
};

const confirmCard: React.CSSProperties = {
  width: "92%",
  maxWidth: 420,
  background: "#020617",
  borderRadius: 18,
  padding: 22,
  textAlign: "center",
  border: "1px solid rgba(148,163,184,0.35)",
  color: "#e5e7eb",
  boxShadow: "0 25px 80px rgba(0,0,0,0.9)",
};

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 12,
  border: "none",
  background: "#dc2626",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
  cursor: "pointer",
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 12,
  border: "1px solid rgba(148,163,184,0.35)",
  background: "transparent",
  color: "#cbd5f5",
  cursor: "pointer",
};


export default GalleryPosting;
